/**
 * @file        src/lib/docAccess.js
 * @description Which customer-facing documents a booking's status allows —
 *              ONE client-approved rule shared by the flight and hotel modules:
 *              pending / failed → none; cancellation requested → none;
 *              confirmed → invoice + voucher/e-ticket + share; cancelled /
 *              refunded → invoice only (the trip no longer exists, so no
 *              travel document and nothing to share). The drawers, the row
 *              menus AND the doc pages themselves all gate on this helper so
 *              a pasted /invoice or /voucher URL can't sidestep the rule.
 * @author      Kinetika Labs
 * @created     2026-08-10
 * @changelog
 *   - 2026-10-09 Claude — A PENDING hotel booking (Awaiting confirmation / Pending / Initiated) shows no voucher and no invoice, the same rule flights follow, until the hotel confirms it.
 *   - 2026-08-10 Claude — Initial (extracted from pages/hotel/cells.jsx so the
 *                          flight module applies the identical rule).
 */

export function docAccess(status) {
  const s = String(status || '').toLowerCase();
  const cancelPending = /cancellation\s*(requested|pending)/.test(s);
  const cancelled = !cancelPending && /cancel|refund/.test(s);
  // Pending is excluded EXPLICITLY: the hotel status "Awaiting confirmation"
  // contains "confirm", so the positive test alone read it as confirmed.
  const pending = !cancelPending && !cancelled && /unconfirm|pend|await|initiat|process|hold/.test(s);
  const confirmed = !cancelPending && !cancelled && !pending && /book|confirm|success/.test(s);
  return {
    confirmed,
    cancelled,
    cancelPending,
    invoice: confirmed || cancelled,
    voucher: confirmed, // the travel document: hotel voucher / flight e-ticket
    share: confirmed,
  };
}
